/** Общие элементы интерфейса: аватар, плитки показателей, постраничная навигация, столбчатый мини-график. */
import type { ReactNode } from 'react';
import type { Minor } from '@mt/protocol';
import { formatDateTime, locale, t } from '../i18n';
import { crn } from '../lib/format';

/** Аватар — инициалы на цвете, стабильно вычисленном из идентификатора. */
export function Avatar({ id, name, size = 40, bot = false }: { id: string; name: string; size?: number; bot?: boolean }) {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const initials = name
    .replace(/^Бот\s+/, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  return (
    <span className={`avatar ${bot ? 'bot' : ''}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: bot ? undefined : `hsl(${h % 360} 45% 38%)` }} aria-hidden="true">
      {bot ? '⚙' : initials || '?'}
    </span>
  );
}

export function Tile({ label, value, tone, hint }: { label: string; value: ReactNode; tone?: 'up' | 'down' | ''; hint?: string }) {
  return (
    <div className="tile" title={hint}>
      <dt>{label}</dt>
      <dd className={`mono ${tone ?? ''}`}>{value}</dd>
    </div>
  );
}

export const toneOf = (v: number): 'up' | 'down' | '' => (v > 0 ? 'up' : v < 0 ? 'down' : '');
export const pct = (v: number | null) => (v === null ? '—' : new Intl.NumberFormat(locale(), { style: 'percent', maximumFractionDigits: 0 }).format(v));
export const signedCr = (v: Minor) => (v > 0 ? '+' : '') + crn(v);

export function dateTime(at: number | null): string {
  if (at === null) return '—';
  return formatDateTime(at);
}

export function ago(at: number | null, now = Date.now()): string {
  if (at === null) return t('common.never');
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return t('common.justNow');
  if (s < 3600) return t('common.minutesAgo', { n: Math.floor(s / 60) });
  if (s < 86400) return t('common.hoursAgo', { n: Math.floor(s / 3600) });
  return t('common.daysAgo', { n: Math.floor(s / 86400) });
}

export function Pager({ offset, limit, total, onPage }: { offset: number; limit: number; total: number; onPage: (offset: number) => void }) {
  if (total <= limit) return null;
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.ceil(total / limit);
  return (
    <nav className="pager" aria-label={t('common.pages')}>
      <button className="btn tiny ghost" disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - limit))}>
        {t('common.back')}
      </button>
      <span className="dim small">
        {t('common.pageOf', { page, pages, total })}
      </span>
      <button className="btn tiny ghost" disabled={offset + limit >= total} onClick={() => onPage(offset + limit)}>
        {t('common.forward')}
      </button>
    </nav>
  );
}

/** Столбики по дням: положительные вверх, отрицательные вниз (или только вверх, если signed=false). */
export function DayBars({ items, value, label, signed = true, format }: { items: { day: string }[]; value: (i: number) => number; label: string; signed?: boolean; format: (v: number) => string }) {
  if (items.length === 0) return <p className="muted-line">{t('common.noData')}</p>;
  const vals = items.map((_, i) => value(i));
  const max = Math.max(1, ...vals.map((v) => Math.abs(v)));
  return (
    <div className={`day-bars ${signed ? 'signed' : ''}`} role="img" aria-label={label}>
      {items.map((it, i) => {
        const v = vals[i]!;
        const h = Math.max(2, Math.round((Math.abs(v) / max) * 100));
        return (
          <div key={it.day} className="day-col" title={`${it.day.split('-').reverse().join('.')}: ${format(v)}`}>
            <div className="day-track">
              <i className={v < 0 ? 'neg' : 'pos'} style={{ height: `${signed ? h / 2 : h}%` }} />
            </div>
            <span className="day-label">{it.day.slice(8)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty-state">{children}</p>;
}
